import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, Subscription, fromEvent, timer } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { VersionInfo } from '../../models/version-info.model';

const VERSION_POLL_INTERVAL_MS = 2 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class VersionService implements OnDestroy {
  private versionInfoSubject = new BehaviorSubject<VersionInfo | null>(null);
  public versionInfo$ = this.versionInfoSubject.asObservable();

  private newVersionSubject = new Subject<VersionInfo>();
  public newVersionAvailable$ = this.newVersionSubject.asObservable();

  private readonly apiUrl = `/api/version`;
  private initialVersionKey: string | null = null;
  private pollingSubscription: Subscription | null = null;
  private visibilitySubscription: Subscription | null = null;
  private newVersionDetected = false;

  constructor(private http: HttpClient) {}

  /**
   * Récupère les informations de version depuis l'API
   * @returns Un Observable contenant les informations de version
   */
  fetchVersionInfo(): Observable<VersionInfo> {
    return this.http.get<VersionInfo>(this.apiUrl).pipe(
      tap(versionInfo => {
        console.log('Version Info:', versionInfo);
        this.versionInfoSubject.next(versionInfo);
      }),
    );
  }

  /**
   * Démarre le polling de version. Idempotent : un seul cycle actif à la fois.
   * Le polling se met en pause quand l'onglet n'est pas visible et reprend
   * lors du retour. Une fois une nouvelle version détectée, le polling s'arrête
   * définitivement et `newVersionAvailable$` émet l'info correspondante.
   */
  startVersionPolling(): void {
    if (this.initialVersionKey !== null) return;

    this.fetchVersionInfo()
      .pipe(take(1))
      .subscribe(info => {
        this.initialVersionKey = this.toVersionKey(info);
        this.startPollingTimer();
        this.bindVisibilityChange();
      });
  }

  /**
   * Formatte la date de déploiement pour un affichage convivial
   * @param dateString La date au format ISO
   * @returns La date formattée
   */
  formatDeploymentDate(dateString: string): string {
    if (dateString === 'Unknown') return 'Date inconnue';

    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', error);
      return dateString;
    }
  }

  ngOnDestroy(): void {
    this.stopPollingTimer();
    this.visibilitySubscription?.unsubscribe();
    this.visibilitySubscription = null;
  }

  private startPollingTimer(): void {
    if (this.newVersionDetected) return;
    if (this.pollingSubscription) return;
    if (typeof document !== 'undefined' && document.hidden) return;

    this.pollingSubscription = timer(VERSION_POLL_INTERVAL_MS, VERSION_POLL_INTERVAL_MS)
      .pipe(switchMap(() => this.fetchVersionInfo()))
      .subscribe(info => this.handlePolledVersion(info));
  }

  private stopPollingTimer(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  private bindVisibilityChange(): void {
    if (typeof document === 'undefined' || this.visibilitySubscription) return;
    this.visibilitySubscription = fromEvent(document, 'visibilitychange').subscribe(() => {
      if (document.hidden) this.stopPollingTimer();
      else this.startPollingTimer();
    });
  }

  private handlePolledVersion(info: VersionInfo): void {
    const currentKey = this.toVersionKey(info);
    if (!this.initialVersionKey || !currentKey) return;
    // Pas de comparaison fiable hors environnement Heroku (createdAt absent)
    if (info.deploymentInfo?.createdAt === 'Unknown') return;
    if (currentKey === this.initialVersionKey) return;

    this.newVersionDetected = true;
    this.stopPollingTimer();
    this.newVersionSubject.next(info);
  }

  private toVersionKey(info: VersionInfo | null): string | null {
    if (!info) return null;
    return `${info.version}|${info.deploymentInfo?.createdAt}`;
  }
}
